import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Assignment as ReturnIcon,
  Person as CustomerIcon,
  ShoppingCart as OrderIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  CurrencyPound as RefundIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AmazonReturnDetails from './AmazonReturnDetails';

const ReturnDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [returnData, setReturnData] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialog, setEditDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [qaDialog, setQaDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [qaCheckNotes, setQaCheckNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (id) {
      fetchReturnDetails();
    }
  }, [id]);

  const fetchReturnDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/returns/${id}`);
      
      if (response.data.success) {
        const returnInfo = response.data.data;
        setReturnData(returnInfo);
        
        // Set items if they exist (already structured array from API)
        if (returnInfo.items) {
          setReturnItems(Array.isArray(returnInfo.items) ? returnInfo.items : []);
        } else if (returnInfo.items_returned) {
          // Fallback for old JSON format
          try {
            const items = JSON.parse(returnInfo.items_returned);
            setReturnItems(Array.isArray(items) ? items : []);
          } catch (e) {
            console.error('Error parsing return items:', e);
            setReturnItems([]);
          }
        } else {
          setReturnItems([]);
        }
      } else {
        setError('Return not found');
      }
    } catch (error) {
      console.error('Error fetching return details:', error);
      setError('Failed to load return details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      const updateData = {
        return_status: newStatus
      };
      
      // Set appropriate date based on status
      if (newStatus === 'received') {
        updateData.received_date = new Date().toISOString();
      } else if (newStatus === 'processed') {
        updateData.processed_date = new Date().toISOString();
      }
      
      await axios.put(`/api/returns/${id}`, updateData);
      
      setStatusDialog(false);
      fetchReturnDetails(); // Refresh data
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update return status');
    }
  };

  const handleQACheckComplete = async () => {
    setIsUpdating(true);
    try {
      const response = await axios.patch(`/api/returns/${id}/status`, {
        status: 'received',
        notes: qaCheckNotes.trim() ? `QA Check: ${qaCheckNotes.trim()}` : 'QA Check completed - no issues noted'
      });
      
      if (response.data.success) {
        setQaDialog(false);
        setQaCheckNotes('');
        fetchReturnDetails(); // Refresh data
      }
    } catch (error) {
      console.error('Error completing QA check:', error);
      setError('Failed to complete QA check');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenQADialog = () => {
    setStatusDialog(false);
    setQaDialog(true);
  };

  const handleEditReturn = async () => {
    try {
      await axios.put(`/api/returns/${id}`, editData);
      setEditDialog(false);
      fetchReturnDetails(); // Refresh data
    } catch (error) {
      console.error('Error updating return:', error);
      setError('Failed to update return details');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      processing: 'info',
      processed: 'success',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  const getPlatform = () => {
    if (returnData?.platform) return returnData.platform;
    if (returnData?.amazon_order_id) return 'Amazon';
    if (returnData?.sellercloud_order_id) return 'Sellercloud';
    if (returnData?.ebay_order_id) return 'eBay';
    return 'Unknown';
  };

  const getOrderId = () => {
    return returnData?.order_id || returnData?.amazon_order_id || returnData?.sellercloud_order_id || returnData?.ebay_order_id || 'N/A';
  };

  const getChannelOrderRef = () => {
    return returnData?.channel_order_ref || 'N/A';
  };

  const getStatusSteps = () => {
    const steps = ['Created', 'Received', 'Processed'];
    const currentStatus = returnData?.return_status || 'pending';
    
    let activeStep = 0;
    if (currentStatus === 'received') activeStep = 1;
    if (currentStatus === 'processed') activeStep = 2;
    if (currentStatus === 'rejected') activeStep = -1; // Special case
    
    return { steps, activeStep };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !returnData) {
    return (
      <Box>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/returns')}
          sx={{ mb: 2 }}
        >
          Back to Returns
        </Button>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Return not found'}
        </Alert>
      </Box>
    );
  }

  const { steps, activeStep } = getStatusSteps();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/returns')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <img 
            src="https://www.xs-stock.co.uk/cdn/shop/files/XS_NEW_LOGO_2ac57727-dd78-4de8-bd11-0c0bb84a6858.png?v=1738612353&width=400" 
            alt="XS Stock"
            style={{
              height: '32px',
              marginRight: '12px',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }}
          />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A365D' }}>
              Return #{returnData.return_number}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Chip 
                label={getPlatform()}
                color={getPlatform() === 'Amazon' ? 'primary' : getPlatform() === 'Sellercloud' ? 'secondary' : 'warning'}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
              <Chip 
                label={returnData.return_status?.toUpperCase() || 'PENDING'} 
                color={getStatusColor(returnData.return_status)}
                size="small"
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              setEditData({
                return_reason: returnData.return_reason,
                notes: returnData.notes,
                refund_amount: returnData.refund_amount
              });
              setEditDialog(true);
            }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            onClick={() => setStatusDialog(true)}
            disabled={returnData.return_status === 'processed'}
          >
            Update Status
          </Button>
        </Box>
      </Box>

      {/* Status Progress */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Return Progress</Typography>
          {returnData.return_status === 'rejected' ? (
            <Alert severity="error">This return has been rejected</Alert>
          ) : (
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Return Information */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <ReturnIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Return Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Return Date"
                    secondary={new Date(returnData.created_at).toLocaleString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Return Reason"
                    secondary={returnData.return_reason}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Refund Amount"
                    secondary={formatCurrency(returnData.refund_amount)}
                  />
                </ListItem>
                {returnData.processed_date && (
                  <ListItem>
                    <ListItemText 
                      primary="Processed Date"
                      secondary={new Date(returnData.processed_date).toLocaleString()}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Information */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <CustomerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Customer Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Name"
                    secondary={returnData.customer_name || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Email"
                    secondary={returnData.customer_email || 'N/A'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Information */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <OrderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Original Order
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Platform"
                    secondary={getPlatform()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Order ID"
                    secondary={getOrderId()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Channel Order Ref"
                    secondary={getChannelOrderRef()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const platform = getPlatform().toLowerCase();
                        let orderId, routePlatform;
                        
                        if (platform === 'ebay') {
                          // For eBay orders, use the eBay order ID (channel_order_ref) and eBay route
                          orderId = getChannelOrderRef();
                          routePlatform = 'ebay';
                        } else {
                          // For other platforms, use internal order ID
                          orderId = getOrderId();
                          routePlatform = platform;
                        }
                        
                        navigate(`/orders/${routePlatform}/${orderId}`);
                      }}
                    >
                      View Order
                    </Button>
                  </ListItemIcon>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Return Items */}
        {returnItems.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Returned Items ({returnItems.length})
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell align="right">Quantity Returned</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {returnItems.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.product_name || item.productName || item.title || 'Unknown Product'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {item.sku || item.sellerSku || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {item.quantity_to_return || item.returnQuantity || item.quantity || 1}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatCurrency(item.unitPrice || (returnData?.refund_amount || 0) / (returnItems?.length || 1))}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatCurrency(item.refund_amount || (returnData?.refund_amount || 0) / (returnItems?.length || 1))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Notes */}
        {(returnData.notes || (returnItems.length > 0 && returnItems[0]?.problem_description)) && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes & Details
                </Typography>
                {returnData.notes && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>General Notes:</strong> {returnData.notes}
                  </Typography>
                )}
                {returnItems.length > 0 && returnItems[0]?.problem_description && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Problem Description:</strong> {returnItems[0].problem_description}
                  </Typography>
                )}
                {returnData.ship_via_rma_id && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>RMA Shipping ID:</strong> {returnData.ship_via_rma_id}
                  </Typography>
                )}
                {returnData.company_name && (
                  <Typography variant="body2">
                    <strong>Company:</strong> {returnData.company_name}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Amazon Return Details - Show formatted view for Amazon returns */}
      {(returnData.platform === 'Amazon Upload' || returnData.platform === 'Amazon SP-API' || returnData.platform === 'Amazon') && (
        <AmazonReturnDetails returnData={returnData} />
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Return Details</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Return Reason"
            value={editData.return_reason || ''}
            onChange={(e) => setEditData({...editData, return_reason: e.target.value})}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Refund Amount"
            type="number"
            value={editData.refund_amount || ''}
            onChange={(e) => setEditData({...editData, refund_amount: parseFloat(e.target.value)})}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={editData.notes || ''}
            onChange={(e) => setEditData({...editData, notes: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditReturn}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
        <DialogTitle>Update Return Status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Current status: <strong>{returnData.return_status}</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleOpenQADialog}
              disabled={returnData.return_status === 'received'}
              startIcon={<EditIcon />}
            >
              Mark as Received (QA Check)
            </Button>
            <Button
              variant="outlined"
              color="success"
              onClick={() => handleStatusUpdate('processed')}
              disabled={returnData.return_status === 'processed'}
              startIcon={<CheckIcon />}
            >
              Mark as Processed
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleStatusUpdate('rejected')}
              disabled={returnData.return_status === 'rejected'}
              startIcon={<CancelIcon />}
            >
              Reject Return
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* QA Check Dialog */}
      <Dialog open={qaDialog} onClose={() => setQaDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            QA Check - Mark as Received
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Complete the QA check for this return and add any notes about the condition of returned items.
            These notes will be available to the CS Team for the refund/exchange process.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="QA Check Notes"
            placeholder="Enter condition notes, damages, or other observations about returned items..."
            value={qaCheckNotes}
            onChange={(e) => setQaCheckNotes(e.target.value)}
            variant="outlined"
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Note: If no issues are found, you can leave this blank and a default "no issues" note will be added.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQaDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleQACheckComplete} 
            variant="contained" 
            startIcon={<CheckIcon />}
            disabled={isUpdating}
          >
            Complete QA Check & Mark Received
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReturnDetails;